import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Footer } from "@/components/rentalmnp/shared/Footer";
import { Logo } from "@/components/rentalmnp/shared/Logo";

const TITLE = "FAQ LMNP — Questions fréquentes sur le statut LMNP";
const DESC =
  "Toutes les réponses sur le statut LMNP : régime fiscal, amortissements, seuils, choix micro-BIC vs réel. Guide complet par RentaLMNP.";
const URL = "https://rentalmnp.com/faq";

const FAQ: Array<{ q: string; a: string }> = [
  {
    q: "Que signifie LMNP ?",
    a: "LMNP signifie « Loueur en Meublé Non Professionnel ». C'est un statut fiscal français qui permet de louer un logement meublé en bénéficiant d'avantages fiscaux importants, à condition que les recettes annuelles ne dépassent pas 23 000 € ou ne représentent pas plus de 50 % des revenus du foyer.",
  },
  {
    q: "Quelle est la différence entre rentabilité brute, nette et nette-nette ?",
    a: "La rentabilité brute = loyers annuels / prix d'achat. La rentabilité nette retranche les charges (taxe foncière, assurance, copropriété…). La rentabilité nette-nette intègre en plus la fiscalité (impôts + prélèvements sociaux) selon votre régime et votre TMI.",
  },
  {
    q: "Micro-BIC ou régime réel : comment choisir ?",
    a: "Si vos charges réelles + amortissements dépassent 50 % de vos loyers (presque toujours en cas de crédit ou de travaux), le régime réel est plus rentable. Sinon, le micro-BIC suffit. Notre simulateur fait la comparaison automatiquement sur votre cas.",
  },
  {
    q: "Le simulateur remplace-t-il un expert-comptable ?",
    a: "Non. RentaLMNP donne une estimation fiable pour orienter votre décision avant l'achat. Une fois le bien acquis et si vous optez pour le régime réel, faire appel à un expert-comptable spécialisé LMNP est recommandé (coût : 400-700 €/an, déductible).",
  },
  {
    q: "Puis-je amortir mon bien en LMNP ?",
    a: "Oui, mais uniquement au régime réel. Le bien est généralement amorti à hauteur de 85 % du prix d'acquisition (la valeur du terrain n'est pas amortissable) sur 25 à 30 ans. Le mobilier s'amortit sur 7 ans, les travaux sur 10 ans.",
  },
  {
    q: "Quel est le seuil pour passer du micro-BIC au réel en LMNP ?",
    a: "Le micro-BIC s'applique automatiquement jusqu'à 77 700 €/an de recettes (barème 2026). Au-delà, le régime réel devient obligatoire. Vous pouvez toutefois opter pour le réel volontairement à tout moment, ce qui est souvent plus avantageux.",
  },
  {
    q: "Le simulateur prend-il en compte mon crédit immobilier ?",
    a: "Oui. Vous pouvez saisir le montant emprunté, le taux, la durée et l'assurance. Le simulateur calcule votre mensualité, intègre les intérêts dans la fiscalité (régime réel) et affiche votre cash-flow réel après remboursement.",
  },
  {
    q: "Mes données sont-elles enregistrées ?",
    a: "Vos simulations sont stockées localement dans votre navigateur (localStorage), jamais sur nos serveurs. Vous pouvez optionnellement saisir un email pour recevoir l'analyse approfondie ; cet email n'est utilisé qu'à cette fin.",
  },
];

export const Route = createFileRoute("/faq")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }),
      },
    ],
  }),
  component: FaqPage,
});

function FaqPage() {
  return (
    <div className="min-h-screen bg-background">
      <main className="mx-auto max-w-4xl px-6 py-8 lg:px-12">
        <div className="mb-6 flex items-center justify-between gap-3">
          <Logo size={36} />
          <Link to="/" className="inline-flex shrink-0 items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-primary">
            <ArrowLeft className="h-4 w-4" /> <span className="hidden sm:inline">Retour à l'accueil</span><span className="sm:hidden">Accueil</span>
          </Link>
        </div>

        <header className="mt-6 mb-10">
          <h1 className="text-3xl font-extrabold text-foreground sm:text-4xl">
            FAQ — Questions fréquentes sur le LMNP
          </h1>
          <p className="mt-4 text-lg text-muted-foreground">
            Tout ce qu'il faut savoir sur le statut LMNP, les régimes fiscaux et notre
            simulateur de rentabilité.
          </p>
        </header>

        <section className="space-y-6">
          {FAQ.map((item) => (
            <article key={item.q} className="rounded-2xl border border-border bg-background p-6 shadow-sm">
              <h2 className="text-lg font-bold text-foreground">{item.q}</h2>
              <p className="mt-2 text-muted-foreground">{item.a}</p>
            </article>
          ))}
        </section>

        <div className="mt-10 rounded-2xl border border-primary/20 bg-primary/5 p-6">
          <h3 className="text-lg font-bold text-foreground">Une autre question ?</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Le mieux est encore de tester votre cas concret. Le simulateur calcule la
            rentabilité, compare les régimes et identifie les leviers d'optimisation.
          </p>
          <Link to="/" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground hover:-translate-y-0.5 hover:bg-primary-dark transition">
            Faire une simulation LMNP <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <nav className="mt-10 flex flex-wrap gap-3 text-sm">
          <Link to="/comparateur-micro-bic-reel" className="font-medium text-primary hover:underline">→ Comparateur micro-BIC vs réel</Link>
          <Link to="/methodologie" className="font-medium text-primary hover:underline">→ Méthodologie de calcul</Link>
        </nav>
      </main>
      <Footer />
    </div>
  );
}
